import request, { type IWorkspaceResponse } from '/@/api/http/request'

/** 机器狗设备列表项（POST /robotDog/list） */
export interface DogDeviceItem {
  id: number
  sn: string
  /** 在线状态："0" 离线，"1" 在线 */
  status: string
  alias: string
  /** 英文别名 */
  nameEn?: string
  device_type: string
  /** 部分接口可能返回 camelCase */
  deviceType?: string
  /** 经纬度接口可能返回数字或字符串 */
  longitude?: string | number | null
  latitude?: string | number | null
  video_url_rtmp: string
  video_url_wss: string
  /** 电池电量百分比，如 "62.0" */
  capacityPercent?: string
  /** 电池告警标识，如 "1" */
  capacityAlarmFlag?: string
  /** 自检状态：0 正常，1 异常（接口可能返回数字或字符串） */
  runStatus?: string | number
  /** 自检异常信息，runStatus 为 1 时有值 */
  errorMessage?: string
}

/** 修改机器狗信息（POST /robotDog/update） */
export interface UpdateDogBody {
  id: number
  sn: string
  alias: string
  deviceType: string
  longitude?: string | number | null
  latitude?: string | number | null
  nameEn: string
}

export interface DogDeviceListParams {
  page: number
  size: number
}

export interface DogDeviceListData {
  list: DogDeviceItem[]
  total?: number
}

// 获取狗的设备列表
export const getDogDeviceList = async function (
  body: DogDeviceListParams
): Promise<IWorkspaceResponse<DogDeviceListData>> {
  const url = '/robotDog/list'
  const result = await request.post(url, body)
  return result.data
}

/** 修改机器狗信息 */
export const updateRobotDog = async function (
  body: UpdateDogBody
): Promise<IWorkspaceResponse<unknown>> {
  const url = '/robotDog/update'
  const result = await request.post(url, body)
  return result.data
}
// 控制狗姿态
export const controlDogPosture = async function (body: {
  deviceSn: string
  posture: string
}): Promise<any> {
  const url = '/robotDog/control/posture'
  const result = await request.post(url, body)
  return result.data
}

// 控制狗方向
export const controlDogDirection = async function (body: {
  deviceSn: string
  command: string
}): Promise<any> {
  const url = '/robotDog/control/move'
  const result = await request.post(url, body)
  return result.data
}

// 启动机器狗巡检任务
export const startInspectionTask = async function (body: {
  sn: string
  resource?: string
}): Promise<any> {
  const url = '/dog/task/create'
  const result = await request.post(url, body)
  return result.data
}

// 停止机器狗巡检任务
export const cancelInspectionTask = async function (body: {
  deviceSn: string
  task_id: string
}): Promise<any> {
  const url = '/robotDog/control/task/cancel'
  const result = await request.post(url, body)
  return result.data
}

// 获取任务列表
export const getTaskList = async function (body: {
  page: number
  size: number
  taskName?: string
  sn?: string
  mapName?: string
}): Promise<any> {
  const url = '/dog/task/list'
  const result = await request.post(url, body)
  return result.data
}

/** 一键复制任务（后端路径若不一致可改此 URL / 入参） */
export const copyDogTask = async function (
  body: { taskId: string },
  config?: { silentBusinessError?: boolean },
): Promise<any> {
  const url = '/dog/task/repeat'
  const result = await request.post(url, body, config)
  return result.data
}

/** 删除任务（POST /dog/task/delete） */
export const deleteDogTask = async function (body: { taskId: string }): Promise<any> {
  const url = '/dog/task/delete'
  const result = await request.post(url, body)
  return result.data
}

// 获取媒体文件列表
export const getMediaFileList = async function (params: {
  objectKey: string
}): Promise<any> {
  const url = '/media/api/v1/files/list'
  const result = await request.get(url, { params })
  return result.data
}

// 获取告警列表
export const getAlarmList = async function (body: {
  page: number
  size: number
  deviceType: number
  taskId?: string
}): Promise<any> {
  const url = '/dog/alarm/list'
  const result = await request.post(url, body)
  return result.data
}

// 获取告警类别数量
export const getAlarmCategoryCount = async function (body: {
  taskId: string
}): Promise<any> {
  const url = '/dog/alarm/label/count'
  const result = await request.get(url, { params: body })
  return result.data
}

// 获取地图数据
export const getMapData = async function (deviceSn: string): Promise<any> {
  const url = `/robotDog/map/query?sn=${deviceSn}`
  const result = await request.get(url)
  return result.data
}

// 获取机器狗的地图列表（POST /dog/map/list，body: { sn }）
export const getDogMapList = async function (body: { sn: string }): Promise<any> {
  const url = '/dog/map/list'
  const result = await request.post(url, body)
  return result.data
}

// 根据地图ID查询点位（POST /dog/map/point/query）
export const getMapPointList = async function (mapId: string): Promise<any> {
  const url = '/dog/map/point/query'
  const result = await request.post(url, { mapId })
  return result.data
}

export interface DogMapPointUpsertItem {
  mapId: string
  sort: number
  name: string
  yaw: number
  x: number
  y: number
}

// 地图点位全量覆盖保存（POST /dog/map/point/create）
export const createMapPointList = async function (body: DogMapPointUpsertItem[]): Promise<any> {
  const url = '/dog/map/point/create'
  const result = await request.post(url, body)
  return result.data
}

/** 单个禁行区：后端接口格式 */
export interface DogMapForbidAreaItem {
  /** 地图ID */
  mapId: string
  /** 排序序号 */
  sort: number
  /** 坐标字符串："{\"x\":1.1,\"y\":2.1},{\"x\":1.1,\"y\":1.1},{\"x\":2.1,\"y\":2.1},{\"x\":2.1,\"y\":1.1}" */
  coordinates: string
}

/** 查询地图禁行区（POST /dog/map/forbid/area/query） body: { mapId } → data: DogMapForbidAreaItem[] */
export const getMapForbiddenZoneList = async function (mapId: string): Promise<any> {
  const url = '/dog/map/forbid/area/query'
  const result = await request.post(url, { mapId })
  return result.data
}

/** 禁行区保存（POST /dog/map/forbid/area/create/{mapId}）body: DogMapForbidAreaItem[] */
export const saveMapForbiddenZoneList = async function (
  mapId: string,
  body: DogMapForbidAreaItem[]
): Promise<any> {
  const url = `/dog/map/forbid/area/create/${mapId}`
  const result = await request.post(url, body)
  return result.data
}

/** POST /dog/task/create 请求体中的航点（与地图点位接口结构一致） */
export interface DogTaskWaypoint {
  id: number
  name: string
  position: [number, number, number]
  timestamp: number
}

// 创建机器狗任务（body: sn、taskName、mapId、waypoints、type）
export const createDogTask = async function (body: {
  sn: string
  taskName: string
  mapId: string
  type: number
  waypoints: DogTaskWaypoint[]
}): Promise<any> {
  const url = '/dog/task/create'
  const result = await request.post(url, body)
  return result.data
}

export interface DogMapItem {
  id: string
  name: string
  /** 与后端 map_minio_path 对应，列表/详情展示用 */
  map_minio_path?: string
  mapMinioPath?: string
  yunMapMinioPath?: string
  pointMinioPath?: string
  mapOriginPath?: string
  yunMapOriginPath?: string
  pointOriginPath?: string
  sn: string
  type?: string
  createTime?: string
}

export interface DogMapListParams {
  page: number
  size: number
  sn?: string
  name?: string
}

// 地图列表（支持分页参数）
export const getDogMapListPage = async function (body: DogMapListParams): Promise<any> {
  const url = '/dog/map/list'
  const result = await request.post(url, body)
  return result.data
}

// 上传地图（批量）
export const createDogMap = async function (body: DogMapItem[]): Promise<any> {
  const url = '/dog/map/create'
  const result = await request.post(url, body)
  return result.data
}

// 删除地图
export const deleteDogMap = async function (id: string): Promise<any> {
  const url = '/dog/map/delete'
  const result = await request.get(url, { params: { id } })
  return result.data
}

// 自动建图开始
export const startDogAutoMap = async function (sn: string): Promise<any> {
  const url = '/dog/map/auto/create'
  const result = await request.get(url, { params: { sn } })
  return result.data
}

// 自动建图停止
export const stopDogAutoMap = async function (sn: string): Promise<any> {
  const url = '/dog/map/auto/stop'
  const result = await request.get(url, { params: { sn } })
  return result.data
}

export interface DogOriginMapItem {
  id: string
  name: string
}

// 获取机器狗端原始地图列表
export const getDogOriginMapList = async function (sn: string): Promise<any> {
  const url = '/dog/map/dog/origin'
  const result = await request.get(url, { params: { sn } })
  return result.data
}

// 同步机器狗地图到平台（批量同步）
export const syncDogOriginMap = async function (body: Array<{
  id: string
  sn: string
}>): Promise<any> {
  const url = '/dog/map/sync'
  const result = await request.post(url, body)
  return result.data
}

// 上传地图文件（POST /dog/map/upload，multipart/form-data）
export const uploadDogMapFile = async function (body: {
  file: File
  mapId: string
}): Promise<any> {
  const url = '/dog/map/upload'
  const formData = new FormData()
  formData.append('file', body.file)
  formData.append('mapId', body.mapId)
  const result = await request.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
  return result.data
}

// 暂停任务
export const pauseDogTask = async function (body: {
  sn: string
  task_id: string
}): Promise<any> {
  const url = '/robotDog/control/task/pause'
  const result = await request.post(url, body)
  return result.data
}

// 继续任务
export const resumeDogTask = async function (body: {
  sn: string
  task_id: string
}): Promise<any> {
  const url = '/robotDog/control/task/continue'
  const result = await request.post(url, body)
  return result.data
}

export interface DogSemanticTarget {
  semantic_id: string
  semantic_target: string
}

// 语义点位任务下发（对应图二接口，type 固定 "1"）
export const createDogSemanticTask = async function (
  body: {
    sn: string
    taskName: string
    mapId: string
    targets: DogSemanticTarget[]
    type?: string
  },
  config?: { silentBusinessError?: boolean },
): Promise<any> {
  const url = '/dog/task/create'
  const result = await request.post(url, { type: '1', ...body }, {
    silentBusinessError: config?.silentBusinessError,
  })
  return result.data
}